import { CAMPAIGNS_API_ENDPOINT } from "../../../constants/constant";
import baseApi from "../../baseApi";
import {
  ApiGetResponse,
  ApiGetResponseById,
  ApiResponse,
  DashboardAnalytics,
  RecommendedCampaign,
  UserQueryParams,
} from "./campaigns.type";

export const campaignApiSlice = baseApi.injectEndpoints({
  endpoints: (build) => ({
    redeemCode: build.mutation<ApiResponse<any>, { code: string }>({
      query: ({ code }) => ({
        url: CAMPAIGNS_API_ENDPOINT.ADD_REDEEM_CODE,
        method: "PUT",
        body: { code },
      }),
      invalidatesTags: ["Campaigns", "Users"],
    }),
    getCampaignsApiCall: build.query<ApiGetResponse, UserQueryParams | void>({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params?.search) queryParams.append("search", params.search);
        if (params?.status) queryParams.append("status", params.status);
        if (params?.campaignStatus)
          queryParams.append("campaignStatus", params.campaignStatus);
        if (params?.campaignId)
          queryParams.append("campaignId", params.campaignId);

        return `${
          CAMPAIGNS_API_ENDPOINT.GET_CAMPAIGNS
        }?${queryParams.toString()}`;
      },
      providesTags: ["Campaigns"],
    }),

    getPrivatedCampaignsApiCall: build.query<
      ApiGetResponse,
      UserQueryParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params?.search) queryParams.append("search", params.search);
        if (params?.status) queryParams.append("status", params.status);
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());
        if (params?.userId)
          queryParams.append("userId", params.userId.toString());

        return `${
          CAMPAIGNS_API_ENDPOINT.GET_ASSIGNED_CAMPAIGNS
        }?${queryParams.toString()}`;
      },
      providesTags: ["Campaigns"],
    }),

    getCodesApiCall: build.query<ApiGetResponse, UserQueryParams | void>({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.limit) queryParams.append("limit", params.limit.toString());
        if (params?.campaignId)
          queryParams.append("campaignId", params.campaignId.toString());

        return `${CAMPAIGNS_API_ENDPOINT.GET_CODES}?${queryParams.toString()}`;
      },
      providesTags: ["Codes"],
    }),

    getCampaignsDashboardApiCall: build.query<
      ApiResponse<DashboardAnalytics>,
      UserQueryParams | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params?.search) queryParams.append("search", params.search);
        if (params?.status) queryParams.append("status", params.status);
        if (params?.campaignStatus)
          queryParams.append("campaignStatus", params.campaignStatus);

        return `${
          CAMPAIGNS_API_ENDPOINT.GET_CAMPAIGNS_DASHBOARD
        }?${queryParams.toString()}`;
      },
      providesTags: ["Campaigns"],
    }),

    getCampaignsByIdApiCall: build.query<
      ApiGetResponseById,
      { campaignId: string }
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params?.campaignId)
          queryParams.append("campaignId", params.campaignId);

        return `${
          CAMPAIGNS_API_ENDPOINT.GET_CAMPAIGN_BY_ID
        }?${queryParams.toString()}`;
      },
      providesTags: ["Campaigns"],
    }),

    getRecommendedCampaignsApiCall: build.query<
      ApiResponse<RecommendedCampaign[]>,
      void
    >({
      query: () => CAMPAIGNS_API_ENDPOINT.GET_RECOMMENDED_CAMPAIGNS,
      providesTags: ["Campaigns"],
    }),

    createCampaignsApiCall: build.mutation<any, any>({
      query: (campaignsBody) => ({
        url: CAMPAIGNS_API_ENDPOINT.CREATE_CAMPAIGN,
        method: "POST",
        body: campaignsBody,
      }),
      invalidatesTags: ["Campaigns"],
    }),

    updateCampaignsApiCall: build.mutation<any, any>({
      query: (campaignsBody) => ({
        url: CAMPAIGNS_API_ENDPOINT.UPDATE_CAMPAIGN,
        method: "PUT",
        body: campaignsBody,
      }),
      invalidatesTags: ["Campaigns"],
    }),

    changeCampaignsStatusApiCall: build.mutation<ApiGetResponse, any>({
      query: (campaignsBody) => ({
        url: `${CAMPAIGNS_API_ENDPOINT.CHANGE_CAMPAIGN_STATUS}?campaignId=${campaignsBody.campaignId}&status=${campaignsBody.status}`,
        method: "PUT",
      }),
      invalidatesTags: ["Campaigns"],
    }),

    claimCampaignsApiCall: build.mutation<ApiGetResponse, any>({
      query: (campaignsBody) => ({
        url: `${CAMPAIGNS_API_ENDPOINT.CLAIM_CAMPAIGN}?campaignId=${campaignsBody.campaignId}`,
        method: "PUT",
        // body: campaignsBody,
      }),
      invalidatesTags: ["Campaigns"],
    }),

    viewSocialsCredentialsApiCall: build.query<ApiGetResponse, UserQueryParams>(
      {
        query: (params) => {
          const queryParams = new URLSearchParams();
          if (params?.platform) queryParams.append("platform", params.platform);
          return `${
            CAMPAIGNS_API_ENDPOINT.VIEW_SOCIAL_CREDNETIALS
          }?${queryParams.toString()}`;
        },
      },
    ),
    updateMediaCredtionalApiCall: build.mutation<any, any>({
      query: (campaignsBody) => ({
        url: CAMPAIGNS_API_ENDPOINT.Update_Media_CREDNETIALS,
        method: "PUT",
        body: campaignsBody,
      }),
      invalidatesTags: ["Campaigns"],
    }),
    disconnectSocialMediaApiCall: build.mutation<any, any>({
      query: (body) => ({
        url: CAMPAIGNS_API_ENDPOINT.DISCONNECT_SOCIAL_MEDIA,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Campaigns"],
    }),
    generateCodes: build.mutation<ApiGetResponse, any>({
      query: ({ campaignId, count, userId }) => ({
        url: CAMPAIGNS_API_ENDPOINT.GENRATE_CODES, // or /campaigns/generate-codes
        method: "POST",
        body: {
          campaignId, // campaignId in body
          codeCounts: count, // how many codes to generate
          userId,
        },
      }),
      invalidatesTags: ["Codes"], // auto-refetch codes list after success
    }),
    getTokenPriceConversionApiCall: build.query<ApiResponse<any>, void>({
      query: () => ({
        url: CAMPAIGNS_API_ENDPOINT.getBinGoldPrice,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useRedeemCodeMutation,
  useGetPrivatedCampaignsApiCallQuery,
  useGetCodesApiCallQuery,
  useGetTokenPriceConversionApiCallQuery,

  useGetCampaignsApiCallQuery,
  useGetCampaignsDashboardApiCallQuery,
  useGetCampaignsByIdApiCallQuery,
  useCreateCampaignsApiCallMutation,
  useUpdateCampaignsApiCallMutation,
  useGetRecommendedCampaignsApiCallQuery,
  useChangeCampaignsStatusApiCallMutation,
  useClaimCampaignsApiCallMutation,
  useViewSocialsCredentialsApiCallQuery,
  useUpdateMediaCredtionalApiCallMutation,
  useDisconnectSocialMediaApiCallMutation,
  useGenerateCodesMutation,
} = campaignApiSlice;

export default campaignApiSlice;
